<template>
  <div id="scrollThing" class="story">
    <h2>Weather Scatterplot</h2>
    <p
      class="intro"
    >While we are are checking the weather every day, we rarely take a look at the big picture: How does the weather look like for a complete year? How does my city compare to other cities?</p>
    <p>The charts below compare the weather of three cities, Montreal, Tokyo and Munich. Just scroll through the page and have fun discovering the visializations.</p>
    <p
      class="instructions"
    >To discover the chart, please roll-over the dots in the scatterplot or use the legend to highlight data of a single month.</p>
    <p
      class="instructions"
    >You can also see the data of different years by clicking on the buttons below the city name.</p>

    <Scrollama
      @step-enter="stepEnterHandler"
      @step-exit="stepExitHandler"
      @step-progress="({ progress }) => (currentStepProgress = progress)"
      class="wrapper"
      :offset="0.3"
      :debug="false"
    >
      <div slot="graphic" class="graphic">
        <div class="viz">
          <!-- <p class="debug">Message: {{message}} /// City: {{selectedCity}}</p> -->
          <h2 v-if="selectedCity">{{selectedCity}}</h2>
          <div class="buttons">
            <a
              @click="changeYear(year,1)"
              v-for="(year,index) in years"
              :key="index"
              v-bind:class="{ selected: selectedYear==year }"
            >{{ year }}</a>
          </div>
          <div class="d3-viz advanced-chapter">
            <div id="tooltip-sp">
              <span id="date"></span>
              <span id="temperature"></span>
            </div>
          </div>
        </div>
      </div>

      <div class="step step2" data-step="Munich">
        <p>Keep scrolling, and let's look at the data of Munich: See the axis changing? Already much warmer.</p>
      </div>

      <div class="step step3" data-step="Tokyo">
        <p>And finally, how does this compare with Tokyo? It gets even hotter!</p>
      </div>

      <div class="step step4 note" data-step="End1">
        <!-- Hidden - animation can play until the end -->
      </div>
    </Scrollama>
    <h2>Radial Weather Chart</h2>
    <p>The radial weather chart shows us the temperature in a circle. In addition to the minimum and maximum temperature we can observe cloud coverage, precipitation and the UV index throughout the year.</p>

    <p>Let's start with Tokyo.</p>

    <Scrollama
      @step-enter="stepEnterHandler"
      @step-exit="stepExitHandler"
      @step-progress="({ progress }) => (currentStepProgress = progress)"
      class="wrapper"
      :offset="0.3"
      :debug="false"
    >
      <div slot="graphic" class="graphic">
        <div class="viz">
          <h2 v-if="selectedCity">{{selectedCity}}</h2>
          <div class="buttons">
            <a
              @click="changeYear(year,2)"
              v-for="(year,index) in years"
              :key="index"
              v-bind:class="{ selected: selectedYear==year }"
            >{{ year }}</a>
          </div>

          <div class="d3-viz advanced-chapter2">
            <div class="tooltip" id="tooltip">
              <div class="tooltip-date" id="tooltip-date"></div>
              <div class="tooltip-temperature" id="tooltip-temperature">
                <span id="tooltip-temperature-min"></span>&nbsp;/
                <span id="tooltip-temperature-max"></span>
              </div>
              <div class="tooltip-metric tooltip-uv">
                <div>UV Index</div>
                <div id="tooltip-uv"></div>
              </div>
              <div class="tooltip-metric tooltip-cloud">
                <div>Cloud Cover</div>
                <div id="tooltip-cloud"></div>
              </div>
              <div class="tooltip-metric tooltip-precipitation-type">
                <div>Precipitation Type</div>
                <div id="tooltip-precipitation-type"></div>
              </div>
              <div class="tooltip-metric tooltip-precipitation">
                <div>Accum. / Intensity</div>
                <div id="tooltip-precipitation"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="step step1" data-step="radMunich">
        <p>Next is Munich.</p>
      </div>

      <div class="step step2" data-step="radMontreal">
        <p>And finally we look at Montreal again, almost 5 month in freezing temperatures.</p>
      </div>

      <div class="step step3 note" data-step="END2">
        <p>The end.</p>
      </div>
    </Scrollama>
    <p class="instructions">Both charts were developped by Amelia Wattenberger and <a target="_blank" href="https://www.fullstack.io/fullstack-d3"> presented in her book D3 and Data Visualizations.</a> This site was created during the beta reader program of the book. </p>
  </div>
</template>

<script>
import "intersection-observer"; // for cross-browser support
import Scrollama from "vue-scrollama";

import db from "@/firebase/init";
import {
  drawMarginalHistogram,
  redrawChart
} from "../d3-scripts/weather-histogram-export";
import {
  drawRadialChart,
  redrawRadialChart
} from "../d3-scripts/weather-radial";

export default {
  name: "Scrollitelling",
  components: {
    Scrollama
  },
  data() {
    return {
      results: [],
      currentStepProgress: null,
      // VIZ DATA
      vizData: null,
      allData: null,
      message: null,
      indexObj: [],
      cities: [],
      years: [],
      selectedCity: "Montreal",
      selectedYear: null
    };
  },
  mounted() {
    this.getIndexFile();
  },
  methods: {
    stepEnterHandler({ element, index, direction }) {
      console.log("ENTER" + element.dataset.step + " / " + direction);
    },
    stepExitHandler({ element, index, direction }) {
      console.log("EXIT: " + element.dataset.step + " / " + direction);
      let data = element.dataset.step;

      if (direction == "up") {
        switch (data) {
          case "Tokyo":
            this.selectedCity = "Munich";
            this.changeCity(1);
            break;
          case "Munich":
            this.selectedCity = "Montreal";
            this.changeCity(1);
            break;
          // Second Graphic
          case "radMontreal":
            this.selectedCity = "Munich";
            this.changeCity(2);
            break;
          case "radMunich":
            this.selectedCity = "Tokyo";
            this.changeCity(2);
            break;
        }
      }

      if (direction == "down") {
        switch (data) {
          case "Munich":
            this.selectedCity = data;
            this.changeCity(1);
            break;
          case "Tokyo":
            this.selectedCity = data;
            this.changeCity(1);
            break;
          case "End1":
            this.selectedCity = "Montreal";

          // Second Graphic
          case "radTokyo":
            this.selectedCity = "Tokyo";
            this.changeCity(2);
            break;
          case "radMunich":
            this.selectedCity = "Munich";
            this.changeCity(2);
            break;
          case "radMontreal":
            this.selectedCity = "Montreal";
            this.changeCity(2);
            break;
          default:
        }
      }
    },
    // VIZ METHODS
    async getFSData(redraw, vizSet) {
      try {
        this.message = "Start Loading";
        let currentDataFile = this.selectedCity + "-" + this.selectedYear;
        let weatherData = await db
          .collection("weather-data")
          .doc(currentDataFile)
          .get();
        this.allData = weatherData.data();
        this.vizData = this.allData.days;
        this.message = "Data loaded from Firestore";

        if (vizSet == 1) {
          redraw
            ? redrawChart(this.vizData)
            : drawMarginalHistogram(
                this.vizData,
                ".advanced-chapter",
                600,
                650
              );
        } else if (vizSet == 2) {
          // load second graph
          redraw
            ? redrawRadialChart(this.vizData)
            : drawRadialChart(this.vizData, ".advanced-chapter2", 600, 650);
        }
      } catch (error) {
        this.message = error;
      }
    },
    async getIndexFile() {
      let indexFile = await db.collection("weather-data").get();
      indexFile.forEach(element => {
        if (element.id != "index") {
          const data = element.data();
          let locationName = data.locationName;
          let year = data.year;
          this.cities.push(locationName);
          this.indexObj.push([locationName, year]);
        }
      });
      // keep only unique cities
      this.cities = this.cities.reduce((prev, current) => {
        // console.log(prev, current, prev.includes(current), [...prev, current]); // uncomment for better understanding
        return prev.includes(current) ? prev : [...prev, current];
      }, []);
      // Select first city as default
      // this.selectedCity = this.cities[0];
      this.selectYears();
      this.selectedYear = this.years[0];

      // Finally build the graphic
      this.getFSData(false, 1);
      this.getFSData(false, 2);
    },
    selectYears() {
      this.years = [];
      let count = 0;
      this.indexObj.forEach((element, i) => {
        if (
          this.indexObj[i][0].toLowerCase() == this.selectedCity.toLowerCase()
        ) {
          this.years[count] = this.indexObj[i][1];
          count++;
        }
      });
      this.years.sort().reverse();
    },
    changeCity(vizSet) {
      this.selectYears();
      this.selectedYear = this.years[0];
      this.getFSData(true, vizSet);
    },
    changeYear(year, vizSet) {
      console.log("change Year to : " + year);
      this.selectedYear = year;
      this.getFSData(true, vizSet);
    }
  }
};
</script>

<style src="vue-scrollama/dist/vue-scrollama.css"></style>
<style lang="stylus">
// Transition
@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

.story {
  max-width: 1600px;
  margin: 20px auto;

  & p {
    margin: 10px auto 30px;
    padding: 10px 20px 10px 40px;
    max-width: 700px;
  }

  & .graphic {
    max-width: 1600px;
    min-width: 400px;
    // height: 60vh;
    display: flex;
    margin: 0px auto 30vh;
    padding-top: 5vh; // space between top and graphic
    animation: fadeIn 3s ease-out 0s 1;

    // border: 1px solid red
    & .viz {
      text-align: center;
      // height: 60vh;
      width: 100%;
      padding: 40px;
      margin: auto;
      position: relative;
      background-image: linear-gradient(
        to bottom,
        #fcfcfc,
        #f7f4f2
      );
      box-shadow: 2px 2px 4px 0px #efe9e8;

      & h2 {
        animation: fadeIn 2s ease-out 0s 1;
        margin-bottom: 18px;
      }

      & .buttons {
        margin: 0 0 20px 0;

        & a {
          padding: 4px 10px;
          margin: 0 6px;
          background-color: #f7ede6;
          border: 3px solid #c4beba;
          font-size: 15px;
          opacity: 0.8;
          transition: 0.5s;
          cursor: pointer;
        }

        & a.selected, a:hover {
          transition: 1s;
          background-color: #d6a884;
          border: 3px solid #bc580b;
          opacity: 1;
        }
      }
    }
  }

  & .step {
    border: 4px solid #bc580b;
    border-radius: 8px;
    margin: 40px auto 110vh;
    max-width: 700px;
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 4px 4px 8px 0px #b2a8a0;

    & p {
      margin: 4px;
      font-size: 18px;
    }
  }

  & .note {
    opacity: 0;
    margin: 40px auto 20vh;
  }

  & .instructions {
    font-size: 15px;
    line-height: 18px;
    margin: 0px auto;
    max-width: 500px;
  }
}

.scrollama-steps {
  pointer-events: none;
}
</style>

<style scoped lang="stylus">
@import './css/d3-styles.styl';
</style>


